/**
 * The Getting Started steps as the page shows them: the onboarding contract's Part B keys (the API's
 * GettingStartedSteps.All holds the same keys, order and titles), what each step is for, where the
 * work is done today and the help articles that go with it.
 */

export interface StepLink {
  label: string;
  route: string;
  /** A screen only PSI staff can open (New Menu): left out for everyone else. */
  psiOnly?: boolean;
}

export interface StepInfo {
  key: string;
  label: string;
  /** One or two sentences: what the step covers. */
  does: string;
  /** The existing screens where the work is done. */
  links: StepLink[];
  /** Help article slugs (src/help/articles). */
  help: string[];
  /** Built in this slice; the others are placeholders that link to today's screens. */
  built?: boolean;
}

export const STEP_INFO: StepInfo[] = [
  {
    key: 'B0-intake',
    label: 'Intake questionnaire',
    does: 'Tell us how your kitchen runs: the meals you serve, your units and rooms, carts, the diets and textures you use, supplements, and who will sign in. Later steps use your answers.',
    links: [],
    help: [],
    built: true,
  },
  {
    key: 'B1-basics',
    label: 'Facility basics',
    does: 'Set your meal names and times, time zone, house diet, NPO diet, regular texture and default DRI group.',
    links: [{ label: 'Facility Settings', route: '/facilities/{facilityId}' }],
    help: [],
    built: true,
  },
  {
    key: 'B2-locations',
    label: 'Units, rooms and tray line',
    does: 'Add your units and the rooms and beds in each one, then set up your carts and put the rooms in tray line order.',
    links: [
      { label: 'Unit Rooms', route: '/unit-rooms' },
      { label: 'Tray Line Setup', route: '/tray-line-setup' },
    ],
    help: ['tray-line-setup'],
    built: true,
  },
  {
    key: 'B3-diets-textures',
    label: 'Diets and textures',
    does: 'Check the diet and texture lists against what your facility orders, and add or retire entries so they match.',
    links: [
      { label: 'Diet Types', route: '/tables/dietTypes' },
      { label: 'Consistencies', route: '/tables/consistencies' },
    ],
    help: ['diet-and-consistency-rules'],
  },
  {
    key: 'B4-allergies',
    label: 'Allergies and food safety',
    does: 'Make sure each food allergy actually takes the food off the tray, warns in Selective Touch and blocks it in self-order. One missing link means no protection and no warning, so this step cannot be skipped and go-live waits for it.',
    links: [
      { label: 'Food Allergies', route: '/tables/foodAllergies' },
      { label: 'Check allergy protection', route: '/allergy-protection' },
    ],
    help: ['allergies-likes-dislikes'],
    built: true,
  },
  {
    key: 'B5-psi-data',
    label: 'PSI data',
    does: "Bring in PSI's recipes, USDA ingredients and reference data, and match them to your own items.",
    links: [{ label: 'Data Update', route: '/settings/dietech-data-update' }],
    help: [],
  },
  {
    key: 'B6-meal-patterns',
    label: 'Meal patterns and supplements',
    does: 'Set up the meal pattern groups your diets use, including the supplements and nourishments you serve.',
    links: [{ label: 'Meal Pattern Groups', route: '/meal-patterns' }],
    help: ['meal-patterns'],
  },
  {
    key: 'B7-menu',
    label: 'Menu',
    // PSI imports the vendor's menu package (New Menu is PSI staff only, Jeff 2026-11-04).
    does: "PSI builds your menu cycle from your vendor's menu package. Review the extension sheet and set the start date.",
    links: [
      { label: 'New Menu', route: '/menucycle/new?tab=import', psiOnly: true },
      { label: 'Menu Cycles', route: '/menucycle' },
      { label: 'Menu Changes', route: '/menucycle/changes' },
    ],
    help: ['importing-a-menu', 'menu-cycles', 'asking-for-a-menu-change'],
  },
  {
    key: 'B8-residents',
    label: 'Residents',
    does: 'Enter your residents with their diet orders, or let your EHR send them.',
    links: [{ label: 'Residents', route: '/people' }],
    help: ['admit-resident', 'change-diet-order'],
  },
  {
    key: 'B9-printing-reports',
    label: 'Printing and reports',
    does: 'Pick the reports you print each day, choose how tray tickets, posting menus and labels look, and choose the printer for each. A sample printout shows each choice as you make it.',
    links: [
      { label: 'Printers', route: '/printers' },
      { label: 'Print Schedules', route: '/print-schedules' },
      { label: 'Reports', route: '/reports' },
    ],
    help: ['tray-tickets', 'running-a-report'],
    built: true,
  },
  {
    key: 'B10-selective-touch',
    label: 'Selective Touch',
    does: 'If servers take orders on tablets, set up the meals, screens, servers, tablets and printing, then prove it with a test order. Skip this step if you do not use Selective Touch.',
    links: [{ label: 'Selective Touch settings', route: '/facilities/{facilityId}?tab=selective-touch' }],
    help: ['selective-touch'],
    built: true,
  },
  {
    key: 'B11-go-live',
    label: 'Go-live check',
    does: 'A last look at the things that stop a first service going well, then sign off that you are ready.',
    links: [],
    help: [],
    built: true,
  },
];

export const INTAKE = 'B0-intake';
export const BASICS = 'B1-basics';
export const LOCATIONS = 'B2-locations';
export const ALLERGIES = 'B4-allergies';
export const PRINTING = 'B9-printing-reports';
export const GO_LIVE = 'B11-go-live';
export const SELECTIVE_TOUCH = 'B10-selective-touch';

export function stepInfo(key: string): StepInfo | undefined {
  return STEP_INFO.find((s) => s.key === key);
}

/** A link's route with the facility filled in. */
export function fillRoute(route: string, facilityId: number): string {
  return route.replace('{facilityId}', String(facilityId));
}
